"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useMutation } from "@tanstack/react-query"
import { SiteEmailsPut, SiteSettingsHeader } from "@services/SiteSettingServices"

interface Field {
  name: string
  label: string
  type: "text" | "email" | "textarea" | "select"
  placeholder?: string
  options?: Array<{ value: string; label: string }>
}

interface Setting {
  id: string
  title: string
  description: string
  fields: Field[]
}

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  setting: Setting
  onSave: (data: Record<string, string>) => void
  initialData?: Record<string, string>
}

export default function SettingsModal({ isOpen, onClose, setting, onSave, initialData = {} }: SettingsModalProps) {
  const [formData, setFormData] = useState<Record<string, string>>(initialData)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const {mutate:siteHeaderSetting} = useMutation({
        mutationFn: SiteSettingsHeader,
      });

  const {mutate:siteEmailSetting} = useMutation({
        mutationFn: SiteEmailsPut,
      });



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    switch(setting.id){
      case "home-header-footer":
      siteHeaderSetting(formData)
      break;
      case "payment-email":
      case "teams-email":
      case "certificate-email":
      siteEmailSetting(formData)
      break;
      default:
        console.log("This is default");
      break;
    }
  
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500))

    onSave(formData)
    setIsSubmitting(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{setting.title}</DialogTitle>
          <DialogDescription>{setting.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {setting.fields.map((field) => (
            <div key={field.name} className="space-y-2">
              <label htmlFor={field.name}>{field.label}</label>

              {field.type === "textarea" ? (
                <>
                <br/>
                <textarea
                  id={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ""}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="min-h-24 w-full mt-2 border-[1px] border-gray-300 rounded-[2px] p-2"
                />
                </>
              ) : field.type === "select" ? (
                <Select
                  value={formData[field.name] || ""}
                  onValueChange={(value) => handleInputChange(field.name, value)}
                >
                  <SelectTrigger id={field.name}>
                    <SelectValue placeholder={field.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={field.name}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ""}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  multiple
                  autoFocus
                />
              )}
            </div>
          ))}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 bg-transparent">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
